import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { Formik, Form } from 'formik'
import { authAPI } from '../services/api'
import { Button, FormikPasswordInput, Alert } from '../components/ui'
import { resetPasswordSchema } from '../utils/validationSchemas'

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [token, setToken] = useState(null)
  const [email, setEmail] = useState(null)

  useEffect(() => {
    const tokenParam = searchParams.get('token')
    const emailParam = searchParams.get('email')

    if (!tokenParam || !emailParam) {
      setError('Invalid or missing reset token. Please request a new password reset link.')
    } else {
      setToken(tokenParam)
      setEmail(emailParam)
    }
  }, [searchParams])

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!token || !email) {
      setError('Invalid reset token')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await authAPI.resetPassword({
        email,
        token,
        password: values.password,
        password_confirmation: values.password_confirmation,
      })

      if (response.data.success) {
        setIsSuccess(true)
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login')
        }, 3000)
      } else {
        setError(response.data.message || 'Failed to reset password')
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.errors?.password?.[0] ||
        err.response?.data?.errors?.token?.[0] ||
        'An error occurred. Please try again.'
      )
    } finally {
      setIsLoading(false)
      setSubmitting(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Password Reset Successful!</h2>
            <p className="text-gray-600 mb-6">
              Your password has been reset successfully. Redirecting to login...
            </p>
            <Link to="/login">
              <Button variant="primary" className="w-full">
                Go to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">LinkBoard</h1>
            <p className="text-gray-600">Set your new password</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4">
              <Alert variant="danger" onClose={() => setError(null)}>
                {error}
              </Alert>
            </div>
          )}

          {!token || !email ? (
            <div className="text-center">
              <Alert variant="danger">
                Invalid or missing reset token. Please request a new password reset link.
              </Alert>
              <Link to="/forgot-password" className="mt-4 inline-block">
                <Button variant="primary">Request New Reset Link</Button>
              </Link>
            </div>
          ) : (
            <Formik
              initialValues={{
                password: '',
                password_confirmation: '',
              }}
              validationSchema={resetPasswordSchema}
              onSubmit={handleSubmit}
              enableReinitialize={false}
            >
              {({ isSubmitting }) => (
                <Form className="space-y-6" noValidate>
                  <FormikPasswordInput
                    id="password"
                    name="password"
                    label="New Password"
                    placeholder="Enter your new password"
                    disabled={isLoading || isSubmitting}
                  />

                  <FormikPasswordInput
                    id="password_confirmation"
                    name="password_confirmation"
                    label="Confirm New Password"
                    placeholder="Confirm your new password"
                    disabled={isLoading || isSubmitting}
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    loading={isLoading || isSubmitting}
                    className="w-full"
                  >
                    Reset Password
                  </Button>
                </Form>
              )}
            </Formik>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-blue-600 hover:text-blue-700">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage

